import { BatchCompatibilityRequest, CompatibilityResponse } from 'ctp-compatibility-checker';

export const normalizeIdentifier = (value: string) => value
  .replace(/[^A-Z0-9-]/gi, '')
  .toUpperCase();

export const normalizeIdentifierForComparison = (value: string) => normalizeIdentifier(value)
  .replace(/-/g, '');

type ClassifiedIdentifiers = {
  payload: BatchCompatibilityRequest;
  invalidIdentifiers: string[];
};

export const classifyIdentifiers = (value: string): ClassifiedIdentifiers => {
  const seenIdentifiers = new Set<string>();
  const identifiers = value
    .split(',')
    .map(item => normalizeIdentifier(item))
    .filter((item) => {
      const comparisonValue = normalizeIdentifierForComparison(item);

      if (!comparisonValue || seenIdentifiers.has(comparisonValue)) {
        return false;
      }

      seenIdentifiers.add(comparisonValue);
      return true;
    });

  const result = identifiers.reduce<{
    licencePlateNumbers: string[];
    vinCodes: string[];
    invalidIdentifiers: string[];
  }>((classified, identifier) => {
    const identifierForComparison = normalizeIdentifierForComparison(identifier);

    if (identifierForComparison.length <= 10) {
      classified.licencePlateNumbers.push(identifier);
    } else if (identifierForComparison.length === 17) {
      classified.vinCodes.push(identifierForComparison);
    } else {
      classified.invalidIdentifiers.push(identifier);
    }

    return classified;
  }, {
    licencePlateNumbers: [],
    vinCodes: [],
    invalidIdentifiers: []
  });

  return {
    payload: {
      ...(result.licencePlateNumbers.length > 0 && {
        licencePlateNumbers: result.licencePlateNumbers
      }),
      ...(result.vinCodes.length > 0 && { vinCodes: result.vinCodes })
    },
    invalidIdentifiers: result.invalidIdentifiers
  };
};

export const partitionCompatibilityResults = (
  compatibilities: CompatibilityResponse[],
  licencePlateNumbers: string[],
  vinCodes: string[]
) => {
  const submittedPlates = new Set(licencePlateNumbers.map(normalizeIdentifierForComparison));
  const submittedVins = new Set(vinCodes.map(normalizeIdentifierForComparison));
  const seenPlates = new Set<string>();
  const seenVins = new Set<string>();

  return compatibilities.reduce((result, compatibility) => {
    const normalizedPlate = normalizeIdentifierForComparison(compatibility.licencePlateNumber || '');
    const normalizedVin = normalizeIdentifierForComparison(compatibility.vin || '');

    if (submittedPlates.has(normalizedPlate) && !seenPlates.has(normalizedPlate)) {
      result.lpn.push(compatibility);
      seenPlates.add(normalizedPlate);
    }
    if (submittedVins.has(normalizedVin) && !seenVins.has(normalizedVin)) {
      result.vin.push(compatibility);
      seenVins.add(normalizedVin);
    }

    return result;
  }, {
    lpn: [] as CompatibilityResponse[],
    vin: [] as CompatibilityResponse[]
  });
};
